<template>
  <b-modal
    v-model="editorShown"
    :title="guarantor ? 'Edit Guarantor' : 'Add Guarantor'"
    @ok="submitGuarantor"
    centered
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok()">
        Save
      </b-button>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="First Name*"
          maxLength="100"
          :state="formErrors.firstName.length == 0 ? null : false"
          :invalid-feedback="formErrors.firstName.join(', ')"
        >
          <template v-slot:label>
            First Name <span class="text-danger">*</span>
          </template>
          <b-form-input
            v-model="form.firstName"
            trim
            :state="formErrors.firstName.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Middle Name" maxLength="100">
          <template v-slot:label>
            Middle Name
          </template>
          <b-form-input v-model="form.middleName" trim></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Last Name*"
          maxLength="100"
          :state="formErrors.lastName.length == 0 ? null : false"
          :invalid-feedback="formErrors.lastName.join(', ')"
        >
          <template v-slot:label>
            Last Name <span class="text-danger">*</span>
          </template>
          <b-form-input
            v-model="form.lastName"
            trim
            :state="formErrors.lastName.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label="DOB"
          :state="formErrors.dateOfBirth.length == 0 ? null : false"
          :invalid-feedback="formErrors.dateOfBirth.join(', ')"
        >
          <template v-slot:label>
            DOB
          </template>
          <date-picker
            v-model="form.dateOfBirth"
            :errors="formErrors.dateOfBirth"
          ></date-picker>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-form-group
          label="SSN"
          :state="formErrors.ssn.length == 0 ? null : false"
          :invalid-feedback="formErrors.ssn.join(', ')"
        >
          <template v-slot:label>
            SSN
          </template>
          <b-form-input
            v-model="form.ssn"
            v-facade="'###-##-####'"
            trim
            :state="formErrors.ssn.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Legal Sex">
          <template v-slot:label>
            Legal Sex
          </template>
          <v-select v-model="form.gender" :options="genders"></v-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-form-group
      label="Relationship"
      :state="formErrors.relationship.length == 0 ? null : false"
      :invalid-feedback="formErrors.relationship.join(', ')"
    >
      <template v-slot:label>
        Relationship to Patient <span class="text-danger">*</span>
      </template>
      <v-select v-model="form.relationship" :options="relationships"></v-select>
    </b-form-group>
    <b-form-group
      v-if="isOtherRelationSelected"
      :state="formErrors.relationshipOther.length == 0 ? null : false"
      :invalid-feedback="formErrors.relationshipOther.join(', ')"
    >
      <b-form-input
        placeholder="Other"
        v-model="form.relationshipOther"
        trim
        maxLength="100"
      >
      </b-form-input>
    </b-form-group>
    <b-form-group
      label="Email"
      :state="formErrors.email.length == 0 ? null : false"
      :invalid-feedback="formErrors.email.join(', ')"
    >
      <template v-slot:label>
        Email
      </template>
      <b-form-input
        v-model="form.email"
        trim
        maxLength="200"
        :state="formErrors.email.length == 0 ? null : false"
      ></b-form-input>
    </b-form-group>
    <b-form-group
      label="Mobile Phone #"
      maxLength="30"
      :state="formErrors.phone.length == 0 ? null : false"
      :invalid-feedback="formErrors.phone.join(', ')"
    >
      <phone-input
        ref="phoneInput"
        defaultCountry="US"
        v-model="form.phone"
        trim
        :state="formErrors.phone.length == 0 ? null : false"
      ></phone-input>
    </b-form-group>
    <b-form-group
      label="Fax"
      :state="formErrors.fax.length == 0 ? null : false"
      :invalid-feedback="formErrors.fax.join(', ')"
    >
      <phone-input
        ref="faxInput"
        defaultCountry="US"
        v-model="form.fax"
        trim
        :state="formErrors.fax.length == 0 ? null : false"
      ></phone-input>
    </b-form-group>
    <b-form-group
      :state="formErrors.address.length == 0 ? null : false"
      :invalid-feedback="formErrors.fax.join(', ')"
    >
      <template v-slot:label>
        Street address
        <span v-if="isPracticeSuiteActive" class="text-danger">*</span>
      </template>
      <b-form-input
        v-model="form.address"
        trim
        maxLength="100"
        :state="formErrors.address.length == 0 ? null : false"
      ></b-form-input>
    </b-form-group>
    <b-form-group
      :state="formErrors.city.length == 0 ? null : false"
      :invalid-feedback="formErrors.fax.join(', ')"
    >
      <template v-slot:label>
        City
        <span v-if="isPracticeSuiteActive" class="text-danger">*</span>
      </template>
      <b-form-input
        v-model="form.city"
        trim
        maxLength="100"
        :state="formErrors.city.length == 0 ? null : false"
      ></b-form-input>
    </b-form-group>
    <b-row>
      <b-col>
        <b-form-group
          :state="formErrors.zipCode.length == 0 ? null : false"
          :invalid-feedback="formErrors.zipCode.join(', ')"
        >
          <template v-slot:label>
            Zip Code
            <span v-if="isPracticeSuiteActive" class="text-danger">*</span>
          </template>
          <b-form-input
            v-model="form.zipCode"
            v-facade="'#####'"
            trim
            :state="formErrors.zipCode.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          :state="formErrors.state.length == 0 ? null : false"
          :invalid-feedback="formErrors.state.join(', ')"
        >
          <template v-slot:label>
            State
            <span v-if="isPracticeSuiteActive" class="text-danger">*</span>
          </template>
          <v-select
            v-model="form.state"
            :options="states"
            :state="formErrors.state.length == 0 ? null : false"
          ></v-select>
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import DatePicker from "../controls/DatePicker";
import PhoneInput from "../controls/PhoneInput.vue";
import {
  validateEmail,
  validatePhoneNumber,
  validateRequired,
  validateZipcode,
  validateSSN,
  validateDate
} from "../../helpers/validation";
export default {
  components: {
    DatePicker,
    PhoneInput
  },
  name: "PatientguarantorForm",
  props: ["guarantor", "guarantors", "patientInfo"],
  data() {
    return {
      form: {
        lastName: "",
        firstName: "",
        middleName: "",
        email: "",
        ssn: "",
        gender: "",
        dateOfBirth: "",
        phone: "",
        fax: "",
        address: "",
        zipCode: "",
        city: "",
        relationship: null,
        relationshipOther: null,
        state: null
      },
      formErrors: {
        lastName: [],
        firstName: [],
        dateOfBirth: [],
        ssn: [],
        relationship: [],
        relationshipOther: [],
        email: [],
        phone: [],
        fax: [],
        zipCode: [],
        address: [],
        city: [],
        state: []
      }
    };
  },
  mounted() {
    if (this.guarantor) {
      this.form = {
        ...this.guarantor,
        relationship: this.guarantor.relationship
          ? {
              value: this.guarantor.relationship.id,
              label: this.guarantor.relationship.name
            }
          : null,
        gender: this.guarantor.gender
          ? {
              value: this.guarantor.gender.id,
              label: this.guarantor.gender.name
            }
          : null,
        state: this.guarantor.state
          ? {
              value: this.guarantor.state.id,
              label: this.guarantor.state.name
            }
          : null
      };
    }
  },
  methods: {
    validateGuarantorSubmit() {
      let withoutErrors = true;

      this.formErrors.firstName = [];
      if (!validateRequired(this.form.firstName)) {
        this.formErrors.firstName.push("This field is required.");
        withoutErrors = false;
      }
      this.formErrors.lastName = [];
      if (!validateRequired(this.form.lastName)) {
        this.formErrors.lastName.push("This field is required.");
        withoutErrors = false;
      }

      this.formErrors.relationship = [];
      if (!validateRequired(this.form.relationship)) {
        this.formErrors.relationship.push("This field is required.");
        withoutErrors = false;
      }

      this.formErrors.relationshipOther = [];
      if (
        this.form.relationship &&
        this.form.relationship.label == "Other" &&
        !validateRequired(this.form.relationshipOther)
      ) {
        this.formErrors.relationshipOther.push("This field is required.");
        withoutErrors = false;
      }

      this.formErrors.email = [];
      if (this.form.email && !validateEmail(this.form.email)) {
        this.formErrors.email.push("Invalid email.");
        withoutErrors = false;
      }

      this.formErrors.ssn = [];
      if (this.form.ssn && !validateSSN(this.form.ssn)) {
        this.formErrors.ssn.push("This field must have a valid SSN format.");
        withoutErrors = false;
      }

      if (this.form.dateOfBirth && !validateDate(this.form.dateOfBirth)) {
        this.formErrors.dateOfBirth.push(
          "This field must have a valid date format."
        );
        withoutErrors = false;
      }

      this.formErrors.phone = [];
      if (
        this.form.phone.trim().length >
          this.activePhoneCountry.dialCode.length + 1 &&
        !validatePhoneNumber(this.form.phone)
      ) {
        this.formErrors.phone.push("Invalid phone number");
        withoutErrors = false;
      }

      this.formErrors.fax = [];
      if (
        this.form.fax.trim().length >
          this.activeFaxCountry.dialCode.length + 1 &&
        !validatePhoneNumber(this.form.fax)
      ) {
        this.formErrors.fax.push("Invalid fax number");
        withoutErrors = false;
      }
      if (
        this.form.fax.trim().length >
          this.activeFaxCountry.dialCode.length + 1 &&
        this.form.phone.trim().length >
          this.activePhoneCountry.dialCode.length + 1 &&
        this.form.fax == this.form.phone
      ) {
        this.formErrors.fax.push("Fax and Phone numbers must be different.");
        withoutErrors = false;
      }

      this.formErrors.zipCode = [];
      if (this.isPracticeSuiteActive && !validateRequired(this.form.zipCode)) {
        this.formErrors.zipCode.push("This field is required.");
        withoutErrors = false;
      }
      if (this.form.zipCode && !validateZipcode(this.form.zipCode)) {
        this.formErrors.zipCode.push(
          "This field must have a valid zipcode format."
        );
        withoutErrors = false;
      }

      this.formErrors.address = [];
      if (this.isPracticeSuiteActive && !validateRequired(this.form.address)) {
        this.formErrors.address.push("This field is required.");
        withoutErrors = false;
      }

      this.formErrors.city = [];
      if (this.isPracticeSuiteActive && !validateRequired(this.form.city)) {
        this.formErrors.city.push("This field is required.");
        withoutErrors = false;
      }

      this.formErrors.state = [];
      if (this.isPracticeSuiteActive && !validateRequired(this.form.state)) {
        this.formErrors.state.push("This field is required.");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitGuarantor(modalEvent) {
      modalEvent.preventDefault();
      const result = this.validateGuarantorSubmit();
      if (result) {
        const guarantorData = {
          ...this.form,
          phone:
            this.form.phone.trim().length >
            this.activePhoneCountry.dialCode.length + 1
              ? this.form.phone
              : "",
          fax:
            this.form.fax.trim().length >
            this.activeFaxCountry.dialCode.length + 1
              ? this.form.fax
              : "",
          relationship: this.form.relationship
            ? {
                id: this.form.relationship.value,
                name: this.form.relationship.label
              }
            : null,
          relationshipOther: !this.isOtherRelationSelected
            ? null
            : this.form.relationshipOther,
          gender: this.form.gender
            ? {
                id: this.form.gender.value,
                name: this.form.gender.label
              }
            : null,
          state: this.form.state
            ? {
                id: this.form.state.value,
                name: this.form.state.label
              }
            : null
        };
        this.$emit("ok", guarantorData);
      }
    }
  },
  computed: {
    ...mapState("layout", {
      isDoseSpotActive: state => state.isDoseSpotActive,
      isPracticeSuiteActive: state => state.isPracticeSuiteActive
    }),
    ...mapState("client", {
      states: state =>
        state.states.map(state => ({
          value: state.id,
          label: state.name
        })),
      genders: state =>
        state.genders.map(gender => ({
          value: gender.id,
          label: gender.name
        })),
      relationships: state =>
        state.relationships.map(relationship => ({
          value: relationship.id,
          label: relationship.name
        }))
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    isOtherRelationSelected: {
      get() {
        return this.form.relationship
          ? this.form.relationship.label === "Other"
          : false;
      }
    },
    activePhoneCountry() {
      return this.$refs.phoneInput.$refs.telInput.activeCountry;
    },
    activeFaxCountry() {
      return this.$refs.faxInput.$refs.telInput.activeCountry;
    }
  },
  watch: {
    "form.relationship": function(newValue, oldValue) {
      if (newValue.label == "Self") {
        this.form.lastName = this.patientInfo.lastName;
        this.form.firstName = this.patientInfo.firstName;
        this.form.middleName = this.patientInfo.middleName ?? "";
        this.form.email = this.patientInfo.email;
        this.form.ssn = this.patientInfo.ssn;
        this.form.gender = this.patientInfo.gender;
        this.form.dateOfBirth = this.patientInfo.dateOfBirth;
        this.form.phone = this.patientInfo.phone;
        this.form.address = this.patientInfo.address;
        this.form.zipCode = this.patientInfo.zipCode;
        this.form.city = this.patientInfo.city;
        this.form.state = this.patientInfo.state;
      } else if (oldValue && oldValue.label == "Self") {
        this.form.lastName = "";
        this.form.firstName = "";
        this.form.middleName = "";
        this.form.email = "";
        this.form.ssn = "";
        this.form.gender = "";
        this.form.dateOfBirth = "";
        this.form.phone = "";
        this.form.address = "";
        this.form.zipCode = "";
        this.form.city = "";
        this.form.state = null;
      }
    }
  }
};
</script>
